import { useEffect, useMemo, useState } from "react";
import { PublicLayout } from "../../components/PublicLayout";
import { api } from "../../lib/api";
import { money } from "../../lib/format";
import { reviewStatusLabel } from "../../lib/adminDisplayLabels";

interface MemberSummary {
  member: null | {
    member_account: string;
    identity_code: string;
    status: string;
    status_label: string;
  };
}

interface BirthdayReview {
  id: number;
  status: string;
  payload_json?: string | null;
  created_at?: string;
  reviewed_at?: string | null;
  reject_reason?: string | null;
  cancel_reason?: string | null;
}

interface BirthdayStatus {
  status: "not_submitted" | "submitted";
  year: number;
  is_eligible: boolean;
  member_status?: string | null;
  review_status?: string | null;
  review_type?: string | null;
  reject_reason?: string | null;
  cancel_reason?: string | null;
  reward_status?: string | null;
  reward_type?: string | null;
  reward_amount?: number | null;
  reward_completed_at?: string | null;
  display_status?: string | null;
  display_message?: string | null;
  review_request: BirthdayReview | null;
}

interface BirthdayApplyResult {
  created: boolean;
  review_request: BirthdayReview;
}

export function ProfilePage() {
  const account = localStorage.getItem("current_member_account") ?? localStorage.getItem("memberAccount") ?? "";
  const [summary, setSummary] = useState<MemberSummary | null>(null);
  const [birthdayStatus, setBirthdayStatus] = useState<BirthdayStatus | null>(null);
  const [birthdayMonth, setBirthdayMonth] = useState("");
  const [birthdayDay, setBirthdayDay] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function load() {
    if (!account) return;
    const [memberData, birthdayData] = await Promise.all([
      api<MemberSummary>(`/api/member/summary?member_account=${encodeURIComponent(account)}`),
      api<BirthdayStatus>(`/api/public/birthday-bonus?member_account=${encodeURIComponent(account)}`)
    ]);
    setSummary(memberData);
    setBirthdayStatus(birthdayData);
    const payload = parseBirthdayPayload(birthdayData.review_request?.payload_json);
    if (payload?.birthday_month) setBirthdayMonth(String(payload.birthday_month));
    if (payload?.birthday_day) setBirthdayDay(String(payload.birthday_day));
  }

  useEffect(() => {
    load().catch((err) => setError(err instanceof Error ? err.message : "基本資料讀取失敗"));
  }, [account]);

  const alreadySubmitted = Boolean(birthdayStatus?.review_request);
  const isEligible = Boolean(birthdayStatus?.is_eligible);
  const canSubmit = isEligible && !alreadySubmitted;
  const birthdayText = useMemo(() => {
    const payload = parseBirthdayPayload(birthdayStatus?.review_request?.payload_json);
    if (!payload) return "";
    return `${payload.birthday_month} 月 ${payload.birthday_day} 日`;
  }, [birthdayStatus]);
  const birthdayInfo = useMemo(() => birthdayFrontendStatus(birthdayStatus), [birthdayStatus]);

  async function submitBirthday() {
    setMessage("");
    setError("");
    try {
      const month = Number(birthdayMonth);
      const day = Number(birthdayDay);
      if (!account) throw new Error("請先登入會員。");
      if (!canSubmit) throw new Error("生日紅包申請尚未開放，請等待後台完成渠道確認。");
      if (!Number.isInteger(month) || month < 1 || month > 12) throw new Error("請選擇生日月份。");
      if (!Number.isInteger(day) || day < 1 || day > 31) throw new Error("請選擇生日日期。");
      const result = await api<BirthdayApplyResult>("/api/public/birthday-bonus", {
        method: "POST",
        body: JSON.stringify({
          member_account: account,
          birthday_month: month,
          birthday_day: day
        })
      });
      setBirthdayStatus({
        status: "submitted",
        year: birthdayStatus?.year ?? new Date().getFullYear(),
        is_eligible: true,
        member_status: summary?.member?.status,
        review_status: result.review_request.status,
        review_request: result.review_request
      });
      setMessage(result.created ? "已送出生日紅包申請，等待後台確認。" : `今年生日紅包已申請，狀態：${reviewStatusLabel(result.review_request.status)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "生日紅包申請送出失敗");
    }
  }

  return (
    <PublicLayout title="基本資料" subtitle="查看會員資料與生日紅包申請狀態">
      {summary?.member ? (
        <>
          <section className="profile-panel llc-card-gradient">
            <span className="hero-kicker">基本資料</span>
            <h2>基本資料</h2>
            <div className="profile-list">
              <div><dt>會員帳號</dt><dd>{summary.member.member_account}</dd></div>
              <div><dt>手機後 3 碼</dt><dd>{summary.member.identity_code}</dd></div>
              <div><dt>目前會員狀態</dt><dd>{summary.member.status_label}</dd></div>
            </div>
          </section>

          <section className="profile-panel">
            <span className="hero-kicker">生日紅包</span>
            <h2>生日紅包申請</h2>
            <p>填寫生日月份與日期後，系統會送出生日紅包申請，待後台人工確認。</p>

            {!isEligible && !alreadySubmitted ? (
              <div className="birthday-status-card warning">
                <strong>生日紅包申請尚未開放</strong>
                <span>請等待後台完成渠道確認後再申請。</span>
              </div>
            ) : null}

            {alreadySubmitted ? (
              <div className={`birthday-status-card ${birthdayInfo.tone}`}>
                <strong>{birthdayInfo.title}</strong>
                <span>{birthdayInfo.description}</span>
                {birthdayText ? <span>生日：{birthdayText}</span> : null}
                {birthdayStatus?.reward_amount ? <span>派發金額：{money(birthdayStatus.reward_amount)}</span> : null}
                {birthdayInfo.reason ? <span>原因：{birthdayInfo.reason}</span> : null}
              </div>
            ) : (
              <>
                <div className="birthday-form-grid">
                  <label>
                    生日月份
                    <select disabled={!canSubmit} value={birthdayMonth} onChange={(event) => setBirthdayMonth(event.target.value)}>
                      <option value="">選擇月份</option>
                      {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => <option value={month} key={month}>{month} 月</option>)}
                    </select>
                  </label>
                  <label>
                    生日日期
                    <select disabled={!canSubmit} value={birthdayDay} onChange={(event) => setBirthdayDay(event.target.value)}>
                      <option value="">選擇日期</option>
                      {Array.from({ length: 31 }, (_, index) => index + 1).map((day) => <option value={day} key={day}>{day} 日</option>)}
                    </select>
                  </label>
                </div>
                <button className="primary-cta profile-submit" disabled={!canSubmit} onClick={submitBirthday}>送出生日紅包申請</button>
              </>
            )}
            {message ? <p className="result">{message}</p> : null}
            {error ? <p className="error-box">{error}</p> : null}
          </section>
        </>
      ) : (
        <section className="login-gate">
          <span className="hero-kicker">基本資料</span>
          <h1>請先登入龍來喬活動大廳</h1>
          <p>登入後才能查看基本資料與生日紅包申請狀態。</p>
          <a className="primary-cta" href="/">前往登入</a>
        </section>
      )}
    </PublicLayout>
  );
}

function birthdayFrontendStatus(status: BirthdayStatus | null) {
  const reviewStatus = status?.review_request?.status ?? status?.review_status;
  const rewardStatus = status?.reward_status;
  if (reviewStatus === "converted_to_reward") {
    if (rewardStatus === "completed") return { title: "生日紅包已完成派發", description: "生日紅包已完成派發。", tone: "" };
    if (rewardStatus === "ready") return { title: "等待人工派發", description: "生日紅包已轉入派發流程，等待人工派發。", tone: "" };
    if (rewardStatus === "failed") return { title: "派發失敗", description: "生日紅包派發失敗，官方將人工處理。", tone: "warning" };
    if (rewardStatus === "canceled") return { title: "派發已取消", description: "生日紅包派發已取消。", tone: "warning" };
    return { title: "已轉入派發流程", description: "生日紅包已轉入派發流程，等待官方處理。", tone: "" };
  }
  if (reviewStatus === "pending") return { title: "生日紅包審核中", description: "生日紅包申請已送出，等待後台確認。", tone: "" };
  if (reviewStatus === "approved") return { title: "生日紅包已通過", description: "生日紅包申請已通過，等待轉入派發流程。", tone: "" };
  if (reviewStatus === "rejected") return { title: "生日紅包未通過", description: "生日紅包申請未通過。", reason: status?.review_request?.reject_reason ?? status?.reject_reason ?? "", tone: "warning" };
  if (reviewStatus === "canceled") return { title: "生日紅包已取消", description: "生日紅包申請已取消。", reason: status?.review_request?.cancel_reason ?? status?.cancel_reason ?? "", tone: "warning" };
  return { title: "尚未申請", description: "今年尚未申請生日紅包。", tone: "" };
}

function parseBirthdayPayload(raw?: string | null) {
  if (!raw) return null;
  try {
    const payload = JSON.parse(raw);
    if (!payload || typeof payload !== "object") return null;
    return payload as { birthday_month?: number; birthday_day?: number; year?: number };
  } catch {
    return null;
  }
}
